import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { analyticsApi, ordersApi } from '../services/api';
import Header from '../components/layout/Header';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Skeleton } from '../components/ui/skeleton';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../components/ui/dialog';
import {
  Clock, AlertTriangle, CheckCircle2,
  Flame, CircleDot, Settings2, Brain
} from 'lucide-react';
import { cn } from '../lib/utils';
import { toast } from 'sonner';

// ─── Helpers ───
const fmtDt = (iso) => {
  if (!iso) return '';
  try { const d = new Date(iso); return `${d.getDate()} ${d.toLocaleString('en-IN',{month:'short'})}, ${String(d.getHours()).padStart(2,'0')}:${String(d.getMinutes()).padStart(2,'0')}`; } catch { return ''; }
};
const fmtDate = (s) => {
  if (!s) return '';
  try { const d = new Date(s); return `${d.getDate()} ${d.toLocaleString('en-IN',{month:'short'})}`; } catch { return ''; }
};

const liveStatus = (dl) => {
  if (!dl || dl.status === 'pending') return 'pending';
  if (dl.completed_at) return 'completed';
  if (!dl.deadline_at) return 'pending';
  try { const r = (new Date(dl.deadline_at).getTime() - Date.now())/1000; return r < 0 ? 'overdue' : r < 6*3600 ? 'at_risk' : 'on_track'; } catch { return 'pending'; }
};

const hrsLeft = (iso) => { try { return Math.round((new Date(iso).getTime()-Date.now())/3600000); } catch { return null; } };

const S = {
  completed: { text:'text-emerald-600 dark:text-emerald-400', bg:'bg-emerald-50 dark:bg-emerald-500/10', border:'border-emerald-200 dark:border-emerald-500/20', icon:CheckCircle2, label:'Done' },
  on_track:  { text:'text-blue-600 dark:text-blue-400', bg:'bg-blue-50 dark:bg-blue-500/10', border:'border-blue-200 dark:border-blue-500/20', icon:CircleDot, label:'On Track' },
  at_risk:   { text:'text-amber-600 dark:text-amber-400', bg:'bg-amber-50 dark:bg-amber-500/10', border:'border-amber-200 dark:border-amber-500/20', icon:AlertTriangle, label:'At Risk' },
  overdue:   { text:'text-red-600 dark:text-red-400', bg:'bg-red-50 dark:bg-red-500/10', border:'border-red-200 dark:border-red-500/20', icon:Flame, label:'Overdue' },
  pending:   { text:'text-slate-400 dark:text-neutral-500', bg:'bg-slate-50 dark:bg-neutral-800', border:'border-slate-200 dark:border-neutral-700', icon:Clock, label:'Waiting' },
};

// ─── AI Nudge — one short line per order ───
const getNudge = (order) => {
  const md = order.materials_detail || {};
  const dls = order.deadlines || {};
  const goldDone = md.gold?.done;
  const diaDone = md.diamonds?.done;
  const chainsDone = md.chains?.done || md.chains?.not_needed;

  for (const [k, dl] of Object.entries(dls)) {
    if (liveStatus(dl) === 'overdue') {
      const h = Math.abs(hrsLeft(dl.deadline_at) || 0);
      if (k === 'casting') return { c: 'text-red-600 dark:text-red-400', msg: `Casting ${h}h overdue — escalate now` };
      if (k === 'material_issuance') return { c: 'text-red-600 dark:text-red-400', msg: `Material dispatch ${h}h overdue` };
      if (k === 'factory_delivery') return { c: 'text-red-600 dark:text-red-400', msg: `Delivery ${h}h past deadline` };
    }
  }
  for (const [k, dl] of Object.entries(dls)) {
    if (liveStatus(dl) === 'at_risk') {
      const h = hrsLeft(dl.deadline_at);
      if (k === 'casting') return { c: 'text-amber-600 dark:text-amber-400', msg: `${h}h left for casting` };
      if (k === 'material_issuance') return { c: 'text-amber-600 dark:text-amber-400', msg: `${h}h left — issue pending materials` };
      if (k === 'factory_delivery') return { c: 'text-amber-600 dark:text-amber-400', msg: `Delivery window closing in ${h}h` };
    }
  }
  if (!goldDone) return { c: 'text-cyan-600 dark:text-cyan-400', msg: md.gold?.issued > 0 ? 'Gold partially issued — complete via Metal Mgmt' : 'Issue gold to start the clock' };
  if (goldDone && (!diaDone || !chainsDone)) {
    const p = [!diaDone && 'diamonds', !chainsDone && 'chains'].filter(Boolean).join(' & ');
    return { c: 'text-cyan-600 dark:text-cyan-400', msg: `Issue ${p} to keep moving` };
  }
  if (goldDone && diaDone && chainsDone) return { c: 'text-emerald-600 dark:text-emerald-400', msg: 'All materials done — on track' };
  return null;
};

// ─── Material Dot ───
const Dot = ({ done, partial, color }) => (
  <span className={cn("inline-block h-2 w-2 rounded-full", done ? "bg-emerald-500" : partial ? cn(color, "animate-pulse") : "bg-slate-300 dark:bg-neutral-600")} />
);

// ─── Phase Cell ───
const PhaseCell = ({ phaseKey, order }) => {
  const md = order.materials_detail || {};

  // Gold Issued — standalone
  if (phaseKey === 'gold_issued') {
    const g = md.gold;
    const status = g?.done ? 'completed' : g?.issued > 0 ? 'at_risk' : 'pending';
    const cfg = S[status]; const Icon = cfg.icon;
    return (
      <div className={cn("flex flex-col items-center justify-center rounded-lg border p-1.5 min-h-[52px]", cfg.bg, cfg.border)}>
        <Icon className={cn("h-3.5 w-3.5 mb-0.5", cfg.text)} />
        {g && !g.done && g.issued > 0
          ? <span className={cn("text-[10px] font-bold tabular-nums", cfg.text)}>{g.issued}/{g.required}g</span>
          : <span className={cn("text-[10px] font-semibold", cfg.text)}>{cfg.label}</span>}
      </div>
    );
  }

  // Post-Cast Materials — diamond + chain with deadline
  if (phaseKey === 'post_cast') {
    const dD = md.diamonds?.done || md.diamonds?.not_needed, cD = md.chains?.done || md.chains?.not_needed;
    const all = dD && cD;
    const dl = order.deadlines?.material_issuance;
    const dlStatus = liveStatus(dl);
    const status = all && dl?.completed_at ? 'completed' : dlStatus !== 'pending' ? dlStatus : (dD || cD) ? 'at_risk' : 'pending';
    const cfg = S[status]; const Icon = cfg.icon;

    // Only show "Done" if both materials are done
    if (all) {
      return (
        <div className={cn("flex flex-col items-center justify-center rounded-lg border p-1.5 min-h-[52px] gap-0.5", S.completed.bg, S.completed.border)}>
          <CheckCircle2 className={cn("h-3.5 w-3.5", S.completed.text)} />
          <span className={cn("text-[10px] font-semibold", S.completed.text)}>Done</span>
        </div>
      );
    }

    // Otherwise always show deadline + material status
    return (
      <div className={cn("flex flex-col items-center justify-center rounded-lg border p-1.5 min-h-[52px] gap-0.5", cfg.bg, cfg.border, status === 'overdue' && "animate-pulse")}>
        {dl?.deadline_at ? (
          <span className={cn("text-[10px] font-bold tabular-nums text-center leading-tight", cfg.text)}>{fmtDt(dl.deadline_at)}</span>
        ) : (
          <Icon className={cn("h-3.5 w-3.5", cfg.text)} />
        )}
        <div className="flex gap-1.5 text-[9px] font-semibold">
          <span className={dD ? "text-emerald-600 dark:text-emerald-400" : "text-amber-600 dark:text-amber-400"}>Dia {dD ? "✓" : "✗"}</span>
          <span className={cD ? "text-emerald-600 dark:text-emerald-400" : "text-violet-600 dark:text-violet-400"}>Ch {cD ? "✓" : "✗"}</span>
        </div>
      </div>
    );
  }

  const dl = order.deadlines?.[phaseKey];
  const status = liveStatus(dl);
  const cfg = S[status]; const Icon = cfg.icon;

  return (
    <div className={cn("flex flex-col items-center justify-center rounded-lg border p-1.5 min-h-[52px]", cfg.bg, cfg.border, status === 'overdue' && "animate-pulse")}>
      <Icon className={cn("h-3.5 w-3.5 mb-0.5", cfg.text)} />
      {dl?.completed_at ? <span className={cn("text-[10px] font-semibold", cfg.text)}>Done</span>
        : dl?.deadline_at ? <span className={cn("text-[10px] font-bold tabular-nums text-center leading-tight", cfg.text)}>{fmtDt(dl.deadline_at)}</span>
        : <span className={cn("text-[10px] font-semibold", cfg.text)}>{cfg.label}</span>}
    </div>
  );
};

// ─── Order Row ───
const OrderRow = ({ order, onConfigure, navigate }) => {
  const ss = Object.values(order.deadlines || {}).map(d => liveStatus(d));
  const urgency = ss.includes('overdue') ? 'overdue' : ss.includes('at_risk') ? 'at_risk' : 'on_track';
  const ucfg = S[urgency];
  const UIcon = ucfg.icon;
  const nudge = getNudge(order);

  return (
    <div
      className={cn("border-b border-border hover:bg-muted/30 transition-colors cursor-pointer group",
        urgency === 'overdue' && "bg-red-50/50 dark:bg-red-500/5",
        urgency === 'at_risk' && "bg-amber-50/50 dark:bg-amber-500/5",
      )}
      onClick={() => navigate(`/orders/${order.order_id}`)}
      data-testid={`timeline-row-${order.co_number}`}
    >
      {/* Desktop */}
      <div className="hidden md:grid grid-cols-[1.4fr_repeat(4,96px)_32px] gap-2.5 items-center px-4 py-2.5">
        <div className="min-w-0">
          <div className="flex items-center gap-1.5 flex-wrap">
            <UIcon className={cn("h-3.5 w-3.5 flex-shrink-0", ucfg.text)} />
            <span className="text-sm font-bold text-foreground truncate">{order.co_number}</span>
            {order.priority === 'Urgent' && <Badge variant="destructive" className="text-[9px] px-1.5 py-0 h-4">URGENT</Badge>}
            {order.delivery_date && <span className="text-[10px] font-semibold text-muted-foreground bg-muted px-1.5 py-0.5 rounded">Due {fmtDate(order.delivery_date)}</span>}
          </div>
          <p className="text-xs text-muted-foreground truncate mt-0.5 pl-5">{order.customer_name} &middot; {order.pieces_ordered} pcs</p>
          {nudge && (
            <p className={cn("text-[11px] mt-1 pl-5 flex items-center gap-1", nudge.c)}>
              <Brain className="h-3 w-3 flex-shrink-0" />{nudge.msg}
            </p>
          )}
        </div>
        {['gold_issued', 'casting', 'post_cast', 'factory_delivery'].map(p => (
          <PhaseCell key={p} phaseKey={p} order={order} />
        ))}
        <button
          className="h-7 w-7 rounded-lg flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted transition-colors opacity-0 group-hover:opacity-100"
          onClick={(e) => { e.stopPropagation(); onConfigure(order); }}
          data-testid={`configure-deadline-${order.co_number}`}
        >
          <Settings2 className="h-3.5 w-3.5" />
        </button>
      </div>

      {/* Mobile */}
      <div className="md:hidden px-3 py-3 space-y-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1.5 min-w-0">
            <UIcon className={cn("h-3.5 w-3.5 flex-shrink-0", ucfg.text)} />
            <span className="text-sm font-bold truncate">{order.co_number}</span>
            {order.priority === 'Urgent' && <Badge variant="destructive" className="text-[9px] px-1 py-0 h-4">URGENT</Badge>}
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            {order.delivery_date && <span className="text-[10px] font-semibold text-muted-foreground bg-muted px-1.5 py-0.5 rounded">Due {fmtDate(order.delivery_date)}</span>}
            <button
              className="h-6 w-6 rounded flex items-center justify-center text-muted-foreground hover:bg-muted"
              onClick={(e) => { e.stopPropagation(); onConfigure(order); }}
            >
              <Settings2 className="h-3 w-3" />
            </button>
          </div>
        </div>
        <p className="text-xs text-muted-foreground">{order.customer_name} &middot; {order.pieces_ordered} pcs</p>
        <div className="grid grid-cols-4 gap-1.5">
          {['gold_issued', 'casting', 'post_cast', 'factory_delivery'].map(p => (
            <div key={p} className="text-center">
              <p className="text-[8px] font-bold uppercase text-muted-foreground mb-0.5">
                {p === 'gold_issued' ? 'Gold' : p === 'casting' ? 'Cast' : p === 'post_cast' ? 'Dia/Ch' : 'Deliv'}
              </p>
              <PhaseCell phaseKey={p} order={order} />
            </div>
          ))}
        </div>
        {nudge && (
          <p className={cn("text-[11px] flex items-start gap-1", nudge.c)}>
            <Brain className="h-3 w-3 flex-shrink-0 mt-0.5" />{nudge.msg}
          </p>
        )}
      </div>
    </div>
  );
};

// ─── Stats Row ───
const Stats = ({ items }) => {
  const s = useMemo(() => {
    let ov=0, ar=0, ot=0;
    items.forEach(o => {
      const ss = Object.values(o.deadlines||{}).map(d=>liveStatus(d));
      if (ss.includes('overdue')) ov++; else if (ss.includes('at_risk')) ar++; else ot++;
    });
    return [{l:'Active',v:items.length,c:S.on_track},{l:'Overdue',v:ov,c:S.overdue},{l:'At Risk',v:ar,c:S.at_risk},{l:'On Track',v:ot,c:S.completed}];
  }, [items]);

  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-2 md:gap-3">
      {s.map(c => { const I=c.c.icon; return (
        <Card key={c.l}>
          <CardContent className="p-3">
            <div className="flex items-center gap-2">
              <div className={cn("h-8 w-8 rounded-lg flex items-center justify-center", c.c.bg)}><I className={cn("h-4 w-4",c.c.text)} /></div>
              <div><p className="text-lg md:text-xl font-bold tabular-nums">{c.v}</p><p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">{c.l}</p></div>
            </div>
          </CardContent>
        </Card>
      ); })}
    </div>
  );
};

// ─── Config Dialog ───
const ConfigDialog = ({ order, open, onClose, onSave }) => {
  const cfg = order?.deadline_config || {};
  const [castH, setCastH] = useState(cfg.casting_hours || 36);
  const [matH, setMatH] = useState(cfg.material_hours || 36);
  const [facD, setFacD] = useState(cfg.factory_days || 8);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (order?.deadline_config) { setCastH(order.deadline_config.casting_hours||36); setMatH(order.deadline_config.material_hours||36); setFacD(order.deadline_config.factory_days||8); }
  }, [order]);

  const handleSave = async () => {
    setSaving(true);
    try { await ordersApi.updateDeadlineConfig(order.order_id, { casting_hours:Number(castH), material_hours:Number(matH), factory_days:Number(facD) }); toast.success('Updated'); onSave(); onClose(); }
    catch { toast.error('Failed'); } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md mx-4">
        <DialogHeader>
          <DialogTitle>Deadline Config — {order?.co_number}</DialogTitle>
          <DialogDescription>Customise deadline windows</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-1.5"><Label>Casting window (hours)</Label><Input type="number" min={1} value={castH} onChange={e=>setCastH(e.target.value)} data-testid="config-casting-hours" /></div>
          <div className="space-y-1.5"><Label>Material issuance window (hours)</Label><Input type="number" min={1} value={matH} onChange={e=>setMatH(e.target.value)} data-testid="config-material-hours" /></div>
          <div className="space-y-1.5"><Label>Factory delivery (working days)</Label><Input type="number" min={1} value={facD} onChange={e=>setFacD(e.target.value)} data-testid="config-factory-days" /></div>
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSave} disabled={saving} data-testid="save-deadline-config">{saving ? 'Saving...' : 'Save'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ─── Main ───
const HEADERS = [
  { key: 'gold_issued', label: 'Gold' },
  { key: 'casting', label: 'Casting' },
  { key: 'post_cast', label: 'Dia & Chain' },
  { key: 'factory_delivery', label: 'Delivery' },
];

const Timeline = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [configOrder, setConfigOrder] = useState(null);
  const [filter, setFilter] = useState('all');

  const fetchData = useCallback(async () => {
    try { const res = await analyticsApi.getTimeline(); setItems(res.data); }
    catch { toast.error('Failed to load timeline'); } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => { const id = setInterval(fetchData, 60000); return () => clearInterval(id); }, [fetchData]);

  const filtered = useMemo(() => items.filter(o => {
    if (filter === 'all') return true;
    const ss = Object.values(o.deadlines||{}).map(d=>liveStatus(d));
    if (filter === 'overdue') return ss.includes('overdue');
    if (filter === 'at_risk') return ss.includes('at_risk') || ss.includes('overdue');
    return true;
  }), [items, filter]);

  if (loading) return (
    <div className="min-h-screen" data-testid="timeline-page">
      <Header title="Timeline" />
      <div className="p-4 md:p-6 space-y-4">
        <div className="grid grid-cols-4 gap-2">{[1,2,3,4].map(i=><Card key={i}><CardContent className="p-3"><Skeleton className="h-10 w-full" /></CardContent></Card>)}</div>
        <Card><CardContent className="p-4"><Skeleton className="h-64 w-full" /></CardContent></Card>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen pb-24 md:pb-20" data-testid="timeline-page">
      <Header title="Timeline" onRefresh={fetchData} />

      <div className="p-4 md:p-6 space-y-4">
        <Stats items={items} />

        <div className="flex gap-2">
          {[{k:'all',l:'All'},{k:'overdue',l:'Overdue'},{k:'at_risk',l:'At Risk'}].map(f=>(
            <button key={f.k} onClick={()=>setFilter(f.k)}
              className={cn("px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors",
                filter===f.k?"bg-foreground text-background":"bg-muted text-muted-foreground hover:bg-muted/80")}
              data-testid={`filter-${f.k}`}>{f.l}</button>
          ))}
        </div>

        <Card>
          <CardContent className="p-0">
            <div className="hidden md:grid grid-cols-[1.4fr_repeat(4,96px)_32px] gap-2.5 px-4 py-2 border-b border-border bg-muted/50">
              <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Order</span>
              {HEADERS.map(h=><span key={h.key} className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground text-center">{h.label}</span>)}
              <span />
            </div>
            <div className="overflow-auto" style={{ maxHeight: 'calc(100vh - 310px)' }}>
              {filtered.length > 0 ? filtered.map(o=>(
                <OrderRow key={o.order_id} order={o} onConfigure={setConfigOrder} navigate={navigate} />
              )) : (
                <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
                  <CheckCircle2 className="h-10 w-10 mb-3 text-emerald-400" />
                  <p className="text-sm font-medium">{filter==='all'?'No active orders':`No ${filter.replace('_',' ')} deadlines`}</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Legend */}
        <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
          <span className="font-semibold">Legend:</span>
          {Object.entries(S).filter(([k])=>k!=='pending').map(([k,c])=>{ const I=c.icon; return <span key={k} className="flex items-center gap-1"><I className={cn("h-3 w-3",c.text)} />{c.label}</span>; })}
          <span className="ml-2 font-semibold">Dia & Chain:</span>
          <span>✓ = Issued</span>
          <span>✗ = Pending</span>
        </div>
      </div>

      <ConfigDialog order={configOrder} open={!!configOrder} onClose={()=>setConfigOrder(null)} onSave={fetchData} />
    </div>
  );
};

export default Timeline;
